import { computed } from "vue";
import { useStore } from "vuex";
import { useEmailWebhooks } from "./useEmailWebhooks";
import type { EmailReceivedPayload } from "@/dtos/EmailWebhook.dto";

/**
 * Enhanced composable for email notifications with store integration
 * Provides unread count tracking and browser notifications
 */
export function useEmailNotifications() {
  const store = useStore();

  // Show browser notification (if permission granted)
  const showBrowserNotification = (payload: EmailReceivedPayload["payload"]): void => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return;
    }

    if (Notification.permission === "granted") {
      const { senderEmail, subject } = payload;
      const body = subject ? `From: ${senderEmail}\nSubject: ${subject}` : `From: ${senderEmail}`;

      try {
        const notification = new Notification("New Email Received", {
          body: body,
          icon: "/favicon.png",
          tag: `email-${payload.emailId}`,
          requireInteraction: false,
        });

        setTimeout(() => {
          notification.close();
        }, 5000);

        // Handle click on notification
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      } catch (error) {
        console.warn("Could not show browser notification:", error);
      }
    }
  };

  // Use existing composable with store integration
  const { isConnected } = useEmailWebhooks({
    onEmailReceived: (payload, helpers) => {
      store.dispatch("email/addEmail", payload);

      // Show browser notification (if permission granted)
      showBrowserNotification(payload);
    },
    showNotifications: true,
  });

  // Computed properties from store
  const unreadCount = computed(() => store.getters["email/unreadCount"]);
  const hasUnread = computed(() => store.getters["email/hasUnread"]);
  const unreadEmails = computed(() => store.getters["email/unreadEmails"]);

  const markAsRead = (emailId: string) => {
    store.dispatch("email/markAsRead", emailId);
  };

  const markAllAsRead = () => {
    store.dispatch("email/markAllAsRead");
  };

  const clearEmails = () => {
    store.dispatch("email/clearEmails");
  };

  return {
    isConnected,
    unreadCount,
    hasUnread,
    unreadEmails,
    markAsRead,
    markAllAsRead,
    clearEmails,
  };
}

/**
 * Request browser notification permission
 * Call this on app initialization
 */
export function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return Promise.resolve("denied" as NotificationPermission);
  }

  if (Notification.permission !== "default") {
    return Promise.resolve(Notification.permission);
  }

  return Notification.requestPermission().then((permission) => {
    return permission;
  });
}
